import type { Step } from "ui";

import { AsyncOperationState } from "fond/async/redux";
import { UploadType } from "fond/types";

import type { TaskPhase, TaskStep } from "./flyout/TaskProgress";
import { GenerationKey } from "./layers";

/** A stage of a task writing to a base data row. */
export type TaskStage = "readingFile" | "downloading" | "uploadingFiles" | "uploading" | "processing" | "savingBoundary" | "deleting";

/** What started a task writing to a base data row, other than generation. */
export type BaseDataTaskSource =
  | "upload"
  | "boundaryImport"
  | "drawnAreaImport"
  | "uploadedAreaImport"
  | "boundaryUpload"
  | "boundarySave"
  | "delete";

/** Stages of each kind of task, in the order they run. */
export const TASK_STAGES: Record<BaseDataTaskSource, TaskStage[]> = {
  upload: ["uploadingFiles", "processing"],
  boundaryImport: ["downloading", "uploading", "processing"],
  drawnAreaImport: ["uploading", "processing"],
  uploadedAreaImport: ["readingFile", "downloading", "uploading", "processing"],
  boundaryUpload: ["readingFile", "savingBoundary"],
  boundarySave: ["savingBoundary"],
  delete: ["deleting"],
};

/** Return a label led by its step's position, e.g. "(2/3) Generating leadins"; a single-step workflow's label has no count. */
export const formatStepLabel = (label: string, { index, count }: Pick<TaskStep, "index" | "count">): string =>
  count > 1 ? `(${index}/${count}) ${label}` : label;

/**
 * Return the task phase of a stage that sends data with an upload: its percent while the bytes transfer, then indeterminate.
 *
 * An upload that hasn't begun is at 0%.
 */
export const getUploadPhase = (upload: UploadType | null | undefined): TaskPhase => {
  const isProcessing = upload?.status === AsyncOperationState.success || (upload?.status === AsyncOperationState.executing && upload.uploadComplete);
  if (isProcessing) return {};
  return { percent: upload?.status === AsyncOperationState.executing ? (upload.percent ?? 0) : 0 };
};

/** Return the steps of a generation run that belong to one generation workflow. */
export const getGenerationKeySteps = (steps: readonly Step[], key: GenerationKey): Step[] =>
  steps.filter(({ ID }) => ID === key || ID.startsWith(`${key}.`));

/** Return the step a run is on, e.g. step 1 of 3 "Cleaning data", or null without steps. */
export const getCurrentStep = (steps: readonly Step[]): TaskStep | null => {
  if (steps.length === 0) return null;
  const inProgress = steps.findIndex(({ Status }) => Status === "InProgress" || Status === "Cancelling");
  const unfinished = steps.findIndex(({ Status }) => Status !== "Success");
  const index = [inProgress, unfinished, steps.length - 1].find((candidate) => candidate >= 0) ?? 0;
  return { label: steps[index].Label, index: index + 1, count: steps.length };
};

/** Return the step a run failed at, or null without steps. */
export const getFailedStep = (steps: readonly Step[]): TaskStep | null => {
  const failed = steps.findIndex(({ Status }) => Status === "Error");
  return failed >= 0 ? { label: steps[failed].Label, index: failed + 1, count: steps.length } : getCurrentStep(steps);
};

/** Return the phase of a running generation workflow, from its own steps of the run. */
export const getGenerationPhase = (steps: readonly Step[], key: GenerationKey): TaskPhase => ({
  step: getCurrentStep(getGenerationKeySteps(steps, key)) ?? { label: "Starting", index: 1, count: 1 },
});
